/**
 * Чистая логика формы `/generate`: разбор id из адреса, выбор платьев, тело оценки, счётчики входов и
 * кадров, подписи оценки, матрица «можно запускать». Потолки (`MAX_INPUTS`, `MAX_IMAGES`) приходят
 * параметрами: `lib/fitting` тянет `@/…`, а модель обязана грузиться под `node --test`.
 */
import type { DressPick, EstimateBody, EstimateResult, GenParams, ModelOption, PicPick, Rule } from "../../../lib/fitting";
import type { ReferenceItem } from "../../../lib/library";
import type { Dress } from "../../../lib/photos";
import type { Pic } from "../../../lib/uploads";

export const NO_PICS: Pic[] = [];

export const pickOf = (p: Pic): PicPick => ({ asset_id: p.asset_id, comment: p.comment });

/**
 * Вставленная картинка в тело запуска: роль пишем ТОЛЬКО когда она не умолчание — тело запуска без
 * портрета остаётся прежним (и ключ оценки у тех, кто портретом не пользуется, не меняется).
 */
export const extraPickOf = (p: Pic): PicPick =>
  p.role && p.role !== "hint" ? { ...pickOf(p), role: p.role } : pickOf(p);

/** Положительные целые без повторов, в порядке появления. */
export const ids = (values: string[]): number[] => {
  const out: number[] = [];
  for (const v of values) {
    const n = Number(v);
    if (Number.isInteger(n) && n > 0 && !out.includes(n)) out.push(n);
  }
  return out;
};

/** Отмеченные фото платья: с превью и не снятые галочкой. */
export const checkedPhotoIds = (d: Dress, off: readonly number[]): number[] =>
  d.photos.filter((p) => p.view_path !== null && !off.includes(p.id)).map((p) => p.id);

/** Миниатюра платья в окне выбора референса (№429): обложка, если она среди отмеченных, иначе первое отмеченное; нет — null. */
export function dressThumb(d: Dress, off: readonly number[]): string | null {
  const on = d.photos.filter((p) => p.view_path !== null && !off.includes(p.id));
  return (on.find((p) => p.id === d.cover_photo_id) ?? on[0])?.asset_path ?? null;
}

/** Платья папок с хотя бы одним отмеченным фото или отмеченной загрузкой; `dresses[i]` — данные папки `dressIds[i]`. */
export function folderPicks(
  dressIds: number[],
  dresses: (Dress | undefined)[],
  unchecked: Record<number, number[]>,
  uploadsByDress: Record<number, Pic[]>,
): DressPick[] {
  return dressIds.flatMap((id, i) => {
    const d = dresses[i];
    if (!d) return [];
    const off = unchecked[id] ?? [];
    const photoIds = checkedPhotoIds(d, off);
    const uploads = (uploadsByDress[id] ?? NO_PICS).filter((p) => p.on).map(pickOf);
    return photoIds.length || uploads.length ? [{ id, photo_ids: photoIds, uploads }] : [];
  });
}

/** Платья из загрузки (папки нет): остаются те, где есть отмеченная картинка. */
export function uploadedPicks(uploaded: { pics: Pic[] }[]): DressPick[] {
  return uploaded.flatMap((d) => {
    const uploads = d.pics.filter((p) => p.on).map(pickOf);
    return uploads.length ? [{ id: null, photo_ids: [], uploads }] : [];
  });
}

/** Тело оценки/запуска; «Что учесть» без сцены — только в режиме `manual`. */
export function buildBody(a: {
  pinIds: number[];
  refs: { id: number }[];
  refNotes: Record<number, string>;
  extras: Pic[];
  manualMode: boolean;
  manualNote: string;
  picks: DressPick[];
  params: GenParams;
}): EstimateBody {
  return {
    pins: a.pinIds,
    refs: a.refs.map((r) => ({ id: r.id, note: a.refNotes[r.id] ?? "" })),
    extras: a.extras.map(extraPickOf),
    note: a.manualMode ? a.manualNote.trim() : "",
    dresses: a.picks,
    params: a.params,
  };
}

/** Пар: сцена × платье (без сцены — одна «пара» на платье). */
export const pairCount = (manualMode: boolean, scenes: number, picks: number): number =>
  (manualMode ? 1 : scenes) * picks;

/** Входов в самой тяжёлой паре: сцена + вставленные + фото платья. */
export const maxInputs = (picks: DressPick[], extrasCount: number, manualMode: boolean): number =>
  Math.max(0, ...picks.map((d) => d.photo_ids.length + d.uploads.length)) + extrasCount + (manualMode ? 0 : 1);

/** Флаги превышения: потолок сервиса, потолок модели (не одновременно), модель недоступна. */
export function inputFlags(
  inputs: number,
  serviceMax: number,
  modelSpec: Pick<ModelOption, "max_inputs" | "disabled"> | undefined,
) {
  const overService = inputs > serviceMax;
  const overModel = !overService && modelSpec !== undefined && inputs > modelSpec.max_inputs;
  const modelOff = modelSpec?.disabled ?? null;
  return { overService, overModel, modelOff, tooManyInputs: overService || overModel || modelOff !== null };
}

/** «Мой» промпт без текста: сервис молча собрал бы авто — не запускаем. */
export const customEmpty = (customMode: boolean, text: string): boolean => customMode && text.trim() === "";

/** Подпись основания оценки. */
export function basisLabel(est: Pick<EstimateResult, "pairs"> | undefined): string {
  return est && est.pairs.length
    ? est.pairs.every((p) => p.basis === "facts")
      ? "по фактам"
      : est.pairs.some((p) => p.basis === "table")
        ? "по таблице пробы"
        : "по формуле"
    : "";
}

/** Размеры/пропорции пар без повторов через запятую. */
export const sizesLabel = (est: Pick<EstimateResult, "pairs"> | undefined): string =>
  est ? [...new Set(est.pairs.map((p) => p.size ?? p.aspect_ratio ?? "—"))].join(", ") : "";

/** Матрица «можно запускать». */
export function canRun(a: {
  pairCount: number;
  tooMany: boolean;
  tooManyInputs: boolean;
  uploading: number;
  launching: boolean;
  noteSaving: number;
  hasEstimate: boolean;
  fromId: number | null;
  customEmpty: boolean;
}): boolean {
  return (
    a.pairCount > 0 &&
    !a.tooMany &&
    !a.tooManyInputs &&
    a.uploading === 0 &&
    !a.launching &&
    a.noteSaving === 0 &&
    a.hasEstimate &&
    a.fromId === null &&
    !a.customEmpty
  );
}

/** Во входах есть внешность модели: у вставленной картинки роль `portrait` (№317). */
export const hasPortrait = (extras: Pic[]): boolean => extras.some((p) => p.role === "portrait");

/**
 * Состояние группы правил на экране (№317). При портрете внешности сервис сам снимает правила с
 * `drop_portrait` («новая нейтральная модель», «другая женщина») — их галочки гаснут, но выбор
 * сотрудника (`value`) не переписывается: уберут портрет с плитки — правило вернётся само.
 */
export function ruleGroup(
  catalog: Rule[],
  value: string[] | null,
  portrait: boolean,
): { chosen: string[]; offered: Rule[]; dropped: Rule[]; live: string[] } {
  const chosen = value ?? catalog.map((r) => r.code);
  const off = (r: Rule) => portrait && r.drop_portrait;
  return {
    chosen,
    offered: catalog.filter((r) => !off(r)),
    dropped: catalog.filter(off),
    live: catalog.filter((r) => !off(r) && chosen.includes(r.code)).map((r) => r.code),
  };
}

/**
 * Референс библиотеки в роли «внешность модели» (№318) — такой же вход `portrait`, как вставленная
 * картинка: сервис берёт ассет по id, поэтому лицо из библиотеки больше не нужно грузить файлом.
 * Комментарий пустой: у портрета подсказку модели пишет сервис, текст сотрудника в промпт не идёт.
 */
export const facePic = (item: ReferenceItem): Pic => ({
  asset_id: item.asset_id,
  path: item.asset_path,
  width: item.asset_width,
  height: item.asset_height,
  comment: "",
  on: true,
  role: "portrait",
});
